import React, {
    useEffect,
    useState,
} from "react";

import {
    FiAlertTriangle,
    FiLoader,
    FiX,
    FiXCircle,
} from "react-icons/fi";

import "./PurchaseBillCancelDialog.css";


const PurchaseBillCancelDialog = ({
    open = false,
    purchaseBill = null,
    loading = false,
    error = "",
    onClose,
    onConfirm,
}) => {
    /*
    |--------------------------------------------------------------------------
    | STATE
    |--------------------------------------------------------------------------
    */

    const [
        cancellationReason,
        setCancellationReason,
    ] = useState("");

    const [
        localError,
        setLocalError,
    ] = useState("");


    /*
    |--------------------------------------------------------------------------
    | RESET
    |--------------------------------------------------------------------------
    */

    useEffect(() => {
        if (open) {
            setCancellationReason("");
            setLocalError("");
        }
    }, [
        open,
        purchaseBill?._id,
    ]);


    /*
    |--------------------------------------------------------------------------
    | ESC KEY + BODY SCROLL
    |--------------------------------------------------------------------------
    */

    useEffect(() => {
        if (!open) {
            return undefined;
        }

        const handleKeyDown = (
            event
        ) => {
            if (
                event.key ===
                    "Escape" &&
                !loading
            ) {
                onClose?.();
            }
        };

        document.addEventListener(
            "keydown",
            handleKeyDown
        );

        const previousOverflow =
            document.body.style
                .overflow;

        document.body.style.overflow =
            "hidden";

        return () => {
            document.removeEventListener(
                "keydown",
                handleKeyDown
            );

            document.body.style.overflow =
                previousOverflow;
        };
    }, [
        open,
        loading,
        onClose,
    ]);


    /*
    |--------------------------------------------------------------------------
    | STATUS
    |--------------------------------------------------------------------------
    */

    const status =
        String(
            purchaseBill
                ?.documentStatus ||
            purchaseBill?.status ||
            ""
        ).toUpperCase();


    /*
    |--------------------------------------------------------------------------
    | BILL NUMBER
    |--------------------------------------------------------------------------
    */

    const billNumber =
        purchaseBill
            ?.documentNumber ||
        purchaseBill
            ?.purchaseBillNumber ||
        "-";


    /*
    |--------------------------------------------------------------------------
    | CONFIRM
    |--------------------------------------------------------------------------
    */

    const handleConfirm = async () => {
        const reason =
            cancellationReason.trim();

        if (!reason) {
            setLocalError(
                "Cancellation reason is required."
            );

            return;
        }

        if (
            typeof onConfirm !==
            "function"
        ) {
            setLocalError(
                "Cancel action is not available."
            );

            return;
        }

        try {
            setLocalError("");

            await onConfirm(
                reason
            );
        } catch (err) {
            setLocalError(
                typeof err === "string"
                    ? err
                    : err?.message ||
                      "Unable to cancel purchase bill."
            );
        }
    };


    /*
    |--------------------------------------------------------------------------
    | CLOSE
    |--------------------------------------------------------------------------
    */

    const handleClose = () => {
        if (loading) {
            return;
        }

        setCancellationReason("");
        setLocalError("");

        onClose?.();
    };


    /*
    |--------------------------------------------------------------------------
    | CLOSED
    |--------------------------------------------------------------------------
    */

    if (!open) {
        return null;
    }


    /*
    |--------------------------------------------------------------------------
    | RENDER
    |--------------------------------------------------------------------------
    */

    return (
        <div
            className="purchase-bill-cancel-overlay"
            role="presentation"
            onMouseDown={(event) => {
                if (
                    event.target ===
                    event.currentTarget
                ) {
                    handleClose();
                }
            }}
        >
            <div
                className="purchase-bill-cancel-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="purchase-bill-cancel-title"
                onMouseDown={(event) =>
                    event.stopPropagation()
                }
            >
                {/* =====================================================
                    HEADER
                ===================================================== */}

                <div className="purchase-bill-cancel-header">
                    <div className="purchase-bill-cancel-header-content">
                        <div className="purchase-bill-cancel-header-icon">
                            <FiXCircle />
                        </div>

                        <div>
                            <small>
                                PURCHASE BILL
                            </small>

                            <h3 id="purchase-bill-cancel-title">
                                Cancel Purchase Bill
                            </h3>

                            <p>
                                {billNumber}
                            </p>
                        </div>
                    </div>

                    <button
                        type="button"
                        className="purchase-bill-cancel-close"
                        disabled={loading}
                        onClick={
                            handleClose
                        }
                        aria-label="Close"
                    >
                        <FiX />
                    </button>
                </div>


                {/* =====================================================
                    BODY
                ===================================================== */}

                <div className="purchase-bill-cancel-body">

                    {/* WARNING */}

                    <div className="purchase-bill-cancel-warning">
                        <div className="purchase-bill-cancel-warning-icon">
                            <FiAlertTriangle />
                        </div>

                        <div>
                            <strong>
                                This action cannot be undone
                            </strong>

                            {status ===
                            "CONFIRMED" ? (
                                <p>
                                    Cancelling this confirmed purchase bill
                                    will reverse the stock and supplier
                                    ledger entries created during
                                    confirmation.
                                </p>
                            ) : (
                                <p>
                                    This purchase bill will be marked as
                                    cancelled and can no longer be edited
                                    or confirmed.
                                </p>
                            )}
                        </div>
                    </div>


                    {/* API / LOCAL ERROR */}

                    {(localError ||
                        error) && (
                        <div className="purchase-bill-cancel-error">
                            <FiAlertTriangle />

                            <span>
                                {localError ||
                                    error}
                            </span>
                        </div>
                    )}


                    {/* REASON */}

                    <div className="purchase-bill-cancel-field">
                        <label htmlFor="purchaseBillCancellationReason">
                            Cancellation Reason
                            <span>
                                *
                            </span>
                        </label>

                        <textarea
                            id="purchaseBillCancellationReason"
                            rows={4}
                            maxLength={500}
                            autoFocus
                            disabled={loading}
                            value={
                                cancellationReason
                            }
                            placeholder="Enter the reason for cancelling this purchase bill..."
                            onChange={(event) => {
                                setCancellationReason(
                                    event.target.value
                                );

                                if (
                                    localError
                                ) {
                                    setLocalError(
                                        ""
                                    );
                                }
                            }}
                        />

                        <div className="purchase-bill-cancel-character-count">
                            {
                                cancellationReason
                                    .length
                            }
                            /500
                        </div>
                    </div>
                </div>


                {/* =====================================================
                    FOOTER
                ===================================================== */}

                <div className="purchase-bill-cancel-footer">
                    <button
                        type="button"
                        className="purchase-bill-cancel-secondary-button"
                        disabled={loading}
                        onClick={
                            handleClose
                        }
                    >
                        Keep Bill
                    </button>

                    <button
                        type="button"
                        className="purchase-bill-cancel-danger-button"
                        disabled={
                            loading ||
                            !cancellationReason
                                .trim()
                        }
                        onClick={
                            handleConfirm
                        }
                    >
                        {loading ? (
                            <>
                                <FiLoader className="purchase-bill-cancel-spinner" />

                                Cancelling...
                            </>
                        ) : (
                            <>
                                <FiXCircle />

                                Confirm Cancellation
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};


export default PurchaseBillCancelDialog;